// src/lib/kinema/core/effects/interpolate.ts
import type { Keyframe } from '../types';
import { applyEasing } from './easing';

export function interpolate<T>(
  keyframes: Keyframe<T>[],
  time: number,
  customLerp?: (from: T, to: T, t: number) => T,
): T | undefined {
  if (keyframes.length === 0) return undefined;
  if (time < keyframes[0].time) return keyframes[0].value;
  if (time >= keyframes[keyframes.length - 1].time) return keyframes[keyframes.length - 1].value;

  // Find the two surrounding keyframes
  let prevIdx = 0;
  for (let i = 1; i < keyframes.length; i++) {
    if (keyframes[i].time > time) break;
    prevIdx = i;
  }

  const prev = keyframes[prevIdx];
  const next = keyframes[prevIdx + 1];
  if (!next) return prev.value;

  // Step easing: hold previous value until we reach the target time
  if (next.easing === 'step') {
    return prev.value;
  }

  // Calculate progress between keyframes
  const progress = (time - prev.time) / (next.time - prev.time);
  const easedProgress = applyEasing(next.easing, progress, next.bezierPoints);

  // Interpolate value
  if (customLerp) {
    return customLerp(prev.value, next.value, easedProgress);
  }

  return defaultLerp(prev.value, next.value, easedProgress);
}

function defaultLerp<T>(from: T, to: T, t: number): T {
  if (typeof from === 'number' && typeof to === 'number') {
    return (from + (to - from) * t) as T;
  }
  // Non-numeric types: snap to nearest
  return t < 0.5 ? from : to;
}
